import { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { playSound, Sound } from '~/assets/sounds';
import {
	getHex,
	IColorSelectProps,
	SelectColorSize,
} from './SelectColor.model';
import {
	StyledArrow,
	StyledButtonContainer,
	StyledColor,
	StyledColorContainer,
	StyledColorsContainer,
	StyledColorWrapper,
	StyledContainer,
	StyledRightContainer,
	StyledWrapper,
} from './SelectColor.styled';

const SelectColor: React.FC<IColorSelectProps> = ({
	items,
	value,
	size = SelectColorSize.Size20,
	count = 4,
	onChange,
}: IColorSelectProps) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const colorListRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = colorListRef.current;
		if (!el) {
			return;
		}

		const onWheel = (e: WheelEvent) => {
			if (el.scrollWidth <= el.clientWidth) {
				return;
			}

			const delta =
				Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
			if (delta === 0) {
				return;
			}

			const maxScrollLeft = el.scrollWidth - el.clientWidth;
			const nextLeft = Math.min(
				maxScrollLeft,
				Math.max(0, el.scrollLeft + delta),
			);

			if (nextLeft === el.scrollLeft) {
				return;
			}

			el.scrollLeft = nextLeft;
			e.preventDefault();
		};

		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, []);

	const handleScroll = useCallback((index: number) => {
		const root = colorListRef.current;
		if (!root) {
			return;
		}

		const colorDiv = root.querySelector(`#color-${index}`);

		colorDiv?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, []);

	const handlePrev = useCallback(() => {
		const newIndex = activeIndex > 0 ? activeIndex - 1 : 0;
		setActiveIndex(newIndex);
		onChange(items[newIndex]);
		handleScroll(newIndex);
	}, [activeIndex, items, onChange, handleScroll]);

	const handleNext = useCallback(() => {
		const newIndex =
			activeIndex < items.length - 1 ? activeIndex + 1 : activeIndex;
		setActiveIndex(newIndex);
		onChange(items[newIndex]);
		handleScroll(newIndex);
	}, [items, activeIndex, onChange, handleScroll]);

	useEffect(() => {
		const index = items.indexOf(value || items[0] || '');
		setActiveIndex(index);
	}, [items, value, handleScroll]);

	useEffectOnce(() => {
		if (!value) {
			return;
		}

		if (items.indexOf(value) >= count) {
			handleScroll(items.indexOf(value));
		}
	});

	return (
		<StyledWrapper $size={size} $count={count}>
			<StyledContainer $size={size}>
				<StyledColorsContainer
					ref={colorListRef}
					$size={size}
					$count={count}
					tabIndex={-1}
				>
					{items.map((item, index) => (
						<StyledColorWrapper $size={size} key={index}>
							<StyledColorContainer
								$size={size}
								onClick={() => {
									onChange(items[index]);
									setActiveIndex(index);
									playSound(Sound.ClickButton);
								}}
							>
								<StyledColor
									$color={getHex(item)}
									$selected={index === activeIndex}
									id={`color-${index}`}
									$size={size}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								/>
							</StyledColorContainer>
						</StyledColorWrapper>
					))}
				</StyledColorsContainer>
				<StyledRightContainer>
					<StyledButtonContainer
						onClick={() => {
							handlePrev();
							playSound(Sound.ClickButton);
						}}
						$disabled={activeIndex === 0}
						$size={size}
						onMouseEnter={() => {
							if (activeIndex === 0) {
								return;
							}
							playSound(Sound.LinkCursor);
						}}
					>
						<StyledArrow />
					</StyledButtonContainer>
					<StyledButtonContainer
						onClick={() => {
							handleNext();
							playSound(Sound.ClickButton);
						}}
						$disabled={activeIndex === items.length - 1}
						$size={size}
						onMouseEnter={() => {
							if (activeIndex === items.length - 1) {
								return;
							}
							playSound(Sound.LinkCursor);
						}}
					>
						<StyledArrow style={{ transform: 'rotate(180deg)' }} />
					</StyledButtonContainer>
				</StyledRightContainer>
			</StyledContainer>
		</StyledWrapper>
	);
};

export { SelectColor };
